import React, { useState, useEffect } from 'react';
import {
  KeyRound, Download, Copy, Check, FileCode2, Loader2,
  AlertTriangle, ListOrdered
} from 'lucide-react';

const REF_PATH = '/reference/MojaloopJwsSigner.java';

const PROFILE = [
  { aspect: 'Algorithm',   value: 'RS256', note: 'RSASSA-PKCS1-v1_5 with SHA-256 (Java: SHA256withRSA)' },
  { aspect: 'Encoding',    value: 'base64url, no padding', note: 'URL-safe alphabet, trailing "=" stripped' },
  { aspect: 'HTTP header', value: 'FSPIOP-Signature', note: 'Carries the JSON below as its value' },
  { aspect: 'Value shape', value: '{"signature","protectedHeader"}', note: 'Both fields base64url-encoded' },
];

const HEADER_FIELDS = [
  ['alg', '"RS256"', 'Fixed. Identifies the signature algorithm.'],
  ['FSPIOP-URI', '/parties/MSISDN/27713803911', 'The resource path of the request being signed.'],
  ['FSPIOP-HTTP-Method', 'POST', 'The HTTP method, upper-cased.'],
  ['FSPIOP-Source', 'yourfsp', 'The sending FSP ID.'],
  ['FSPIOP-Destination', 'payeefsp', 'The receiving FSP ID — omitted entirely when there is no destination (e.g. a party lookup to the hub).'],
  ['Date', 'Tue, 24 Jun 2026 09:00:00 GMT', 'Must be byte-identical to the HTTP Date header sent on the wire.'],
];

const STEPS = [
  ['Build the protected header', 'An ordered JSON object: alg, FSPIOP-URI, FSPIOP-HTTP-Method, FSPIOP-Source, [FSPIOP-Destination], Date.'],
  ['base64url-encode header and body', 'encodedHeader = base64url(headerJson); encodedPayload = base64url(requestBody). No padding.'],
  ['Form the signing input', 'signingInput = encodedHeader + "." + encodedPayload — the JWS signing string.'],
  ['Sign with RS256', 'signature = base64url( RSA-SHA256(signingInput) ), signed with your private key.'],
  ['Assemble the header value', 'FSPIOP-Signature: {"signature":"<sig>","protectedHeader":"<encodedHeader>"}'],
  ['Send the exact signed bytes', 'Transmit the SAME request-body bytes you signed. Any re-serialization invalidates the signature.'],
];

const PITFALLS = [
  ['Signed bytes ≠ wire bytes', 'The #1 cause of verification failures. If you serialize the body once to sign and again to send (pretty-print, key reorder, added whitespace), the digest no longer matches. Sign the exact byte array you put on the wire.'],
  ['Padded or standard base64', 'FSPIOP uses base64URL without padding. Standard base64 (+ / =) or trailing "=" will fail verification.'],
  ['Date mismatch', 'The Date in the protected header must equal the HTTP Date header exactly — same format, same value. Generate it once and reuse it.'],
  ['Sending FSPIOP-Destination when absent', 'For requests with no destination (party lookups to the hub), the field must be omitted from the header object — not sent as null or "".'],
  ['HTTP method casing', 'FSPIOP-HTTP-Method must be upper-cased (POST, PUT, GET). Lower-case breaks the canonical header.'],
];

export default function FspiopSigningGuide() {
  const [code, setCode]     = useState('');
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(REF_PATH)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(t => { setCode(t); setLoad(false); })
      .catch(e => { setError(e.message); setLoad(false); });
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-primary-600" />
          FSPIOP Signing Guide (JWS)
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          How to produce the <span className="font-mono text-xs">FSPIOP-Signature</span> header the
          Mojaloop hub expects on every signed request — the JWS profile, the protected-header
          claims, and the byte-exactness traps, with a reference implementation.
        </p>
      </div>

      {/* Profile */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Signature profile</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {PROFILE.map((p, i) => (
                <tr key={p.aspect} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2.5 font-medium text-gray-700 w-36 align-top">{p.aspect}</td>
                  <td className="px-4 py-2.5 text-gray-900">
                    <span className="font-mono text-xs">{p.value}</span>
                    <div className="text-xs text-gray-400 mt-0.5">{p.note}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* JWS construction diagram */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">How the signature is built</h2>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
          <svg viewBox="0 0 680 188" width="100%" role="img"
               aria-label="JWS construction: encoded header dot encoded payload, signed with RS256, assembled into the FSPIOP-Signature header">
            {/* header + payload */}
            <rect x="20" y="16" width="300" height="40" rx="6" fill="#eff6ff" stroke="#2563eb" strokeWidth="1" />
            <text x="170" y="34" fontSize="12" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#1e40af">base64url(protectedHeader)</text>
            <text x="170" y="49" fontSize="11" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#3b82f6">alg, FSPIOP-URI, Method, Source…</text>

            <text x="332" y="42" fontSize="18" textAnchor="middle" fill="#64748b">.</text>

            <rect x="346" y="16" width="300" height="40" rx="6" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1" />
            <text x="496" y="34" fontSize="12" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#166534">base64url(requestBody)</text>
            <text x="496" y="49" fontSize="11" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#22c55e">the exact JSON sent on the wire</text>

            {/* arrow down */}
            <line x1="333" y1="60" x2="333" y2="86" stroke="#94a3b8" strokeWidth="1.5" />
            <path d="M333,92 L329,84 L337,84 Z" fill="#94a3b8" />
            <text x="345" y="80" fontSize="11" fontFamily="ui-monospace, monospace" fill="#64748b">signingInput</text>

            {/* RS256 */}
            <rect x="233" y="92" width="200" height="34" rx="6" fill="#fff7ed" stroke="#d97706" strokeWidth="1" />
            <text x="333" y="113" fontSize="12" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#92400e">RS256 sign (private key)</text>

            {/* arrow down */}
            <line x1="333" y1="126" x2="333" y2="146" stroke="#94a3b8" strokeWidth="1.5" />
            <path d="M333,152 L329,144 L337,144 Z" fill="#94a3b8" />

            {/* result */}
            <rect x="120" y="152" width="426" height="30" rx="6" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1" />
            <text x="333" y="171" fontSize="12" textAnchor="middle" fontFamily="ui-monospace, monospace" fill="#334155">FSPIOP-Signature: {'{'}"signature","protectedHeader"{'}'}</text>
          </svg>
        </div>
      </section>

      {/* Protected header fields */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Protected header claims</h2>
        <p className="text-sm text-gray-600 mb-3">
          The header is an ordered JSON object. Field names and casing are exact.
        </p>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {HEADER_FIELDS.map(([k, ex, desc], i) => (
                <tr key={k} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2.5 font-mono text-xs text-primary-700 align-top whitespace-nowrap">{k}</td>
                  <td className="px-4 py-2.5 align-top">
                    <div className="font-mono text-xs text-gray-500">{ex}</div>
                    <div className="text-sm text-gray-700 mt-0.5">{desc}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Steps */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ListOrdered className="w-5 h-5 text-gray-400" /> Signing process
        </h2>
        <ol className="space-y-2">
          {STEPS.map(([title, desc], i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="text-sm text-gray-600">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Pitfalls */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Common pitfalls
        </h2>
        <div className="space-y-2">
          {PITFALLS.map(([title, desc]) => (
            <div key={title} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-900">{title}</p>
              <p className="text-sm text-amber-800 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reference */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileCode2 className="w-5 h-5 text-gray-400" /> Reference implementation
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={copyCode} disabled={!code}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a href={REF_PATH} download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
              <Download className="w-4 h-4" /> Download .java
            </a>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          <span className="font-mono text-xs">MojaloopJwsSigner.java</span> — the connector's
          production JWS signing logic. The methods to mirror are
          <span className="font-mono text-xs"> buildProtectedHeader()</span>,
          <span className="font-mono text-xs"> base64UrlEncode()</span>, and
          <span className="font-mono text-xs"> sign()</span>.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reference source…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            Could not load reference source: {error}
          </div>
        )}
        {!loading && !error && (
          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-[480px] leading-relaxed">
            {code}
          </pre>
        )}
      </section>
    </div>
  );
}
